import { ChevronDown, MapPin, Clock, Star, Shield, Sparkles, Video, Home, Zap, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import { useNavigate } from "react-router-dom";
import AnimatedCounter from "@/components/AnimatedCounter";
import FloatingParticles from "@/components/FloatingParticles";

const HeroSection = ({ scrollToSection, openBookingModal, openQuiz, openVirtualConsultation, openWaitlist, openLastMinuteClub, lastMinuteCount = 0 }) => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  // Time-based greeting
  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    if (language === "fr") {
      if (hour >= 5 && hour < 12) return "Bonjour";
      if (hour >= 12 && hour < 17) return "Bon après-midi";
      if (hour >= 17 && hour < 21) return "Bonsoir";
      return "Bonne nuit";
    } else {
      if (hour >= 5 && hour < 12) return "Good Morning";
      if (hour >= 12 && hour < 17) return "Good Afternoon";
      if (hour >= 17 && hour < 21) return "Good Evening";
      return "Welcome";
    }
  };

  const stats = [
    { icon: MapPin, value: "3", label: t("hero.locations"), isNumber: true },
    { icon: Clock, value: "35", suffix: "+", label: t("hero.years"), isNumber: true },
    { icon: Star, value: "100", suffix: "+", label: t("hero.reviews"), isNumber: true },
    { icon: Shield, value: "ACLS", label: t("hero.certified"), isNumber: false },
  ];

  return (
    <section
      id="hero"
      className="relative min-h-screen md:min-h-screen flex items-center pt-8 md:pt-12"
      data-testid="hero-section"
    >
      {/* Background Image with Parallax */}
      <div
        className="absolute inset-0 bg-cover parallax-hero hero-portrait-mobile"
        style={{
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_8776705b-3965-48ce-aa62-68a5febfba36/artifacts/y3zzbltg_IMG_4662.jpeg')`,
          backgroundPosition: "center top",
        }}
      >
        {/* Centered dark overlay box */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" />
        </div>
        
        {/* Floating Particles - Hidden on mobile for performance */}
        <div className="hidden md:block">
          <FloatingParticles />
        </div>
      </div>

      {/* Content - Centered */}
      <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-16 w-full z-20">
        <div className="max-w-3xl mx-auto text-center">
          
          {/* ===== MOBILE LAYOUT (Same as desktop, optimized for mobile) ===== */}
          <div className="md:hidden">
            {/* Time-Based Greeting */}
            <div className="mb-2 animate-fade-in">
              <span 
                className="text-white text-lg tracking-wide"
                style={{ fontFamily: "'Playfair Display', serif", textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}
              >
                {getTimeBasedGreeting()}, {language === "fr" ? "Bienvenue" : "Welcome"}
              </span>
            </div>

            {/* Badge */}
            <div className="mb-4 animate-fade-in">
              <span 
                className="text-amber-400 text-sm tracking-[0.2em] uppercase animate-pulse-glow inline-block px-3 py-1.5 rounded-full font-medium"
                style={{ fontFamily: "'Playfair Display', serif", textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}
              >
                ✦ {t("hero.experience")} ✦
              </span>
            </div>

            {/* Main Heading - Larger */}
            <h1
              className="text-[2.75rem] text-white mb-0 animate-fade-in-up leading-tight"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400, textShadow: '2px 2px 4px rgba(0,0,0,0.4)' }}
            >
              {language === "fr" ? "Révélez Votre" : "Reveal Your"} <span className="italic text-gradient-animate">{language === "fr" ? "Meilleure Version" : "Best Self"}</span>
            </h1>
            
            {/* Script subtitle - Larger */}
            <p
              className="text-[2rem] text-white mb-3 animate-fade-in-up -mt-1"
              style={{ fontFamily: "'Great Vibes', cursive", fontWeight: 400, textShadow: '1px 1px 3px rgba(0,0,0,0.3)' }}
            >
              {language === "fr" ? "Naturellement. En Confiance." : "Naturally. Confidently."}
            </p>

            {/* Subheading - Value proposition */}
            <p 
              className="text-base text-white mb-3 animate-fade-in-up animation-delay-200 max-w-2xl mx-auto leading-relaxed px-2"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300, textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}
            >
              {language === "fr" 
                ? "Imaginez vous regarder dans le miroir et aimer ce que vous voyez. Des résultats subtils et raffinés qui rehaussent votre beauté naturelle — sans que personne ne sache pourquoi vous rayonnez."
                : "Imagine looking in the mirror and loving what you see. Subtle, refined results that enhance your natural beauty — without anyone knowing why you're glowing."}
            </p>
            
            {/* Trust line */}
            <p 
              className="text-sm text-amber-400 mb-6 animate-fade-in-up animation-delay-300 max-w-xl mx-auto px-2 font-medium"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}
            >
              {language === "fr" 
                ? "35+ ans d'expertise en soins infirmiers • Une des injectrices les plus expérimentées d'Ottawa"
                : "35+ years of nursing expertise • One of Ottawa's most experienced injectors"}
            </p>

            {/* Stats Row - Same as desktop */}
            <div className="grid grid-cols-4 gap-2 mb-6 stagger-fade-in px-2">
              {stats.map((stat, index) => (
                <div 
                  key={index} 
                  className="flex flex-col items-center gap-1 stat-item"
                  style={{ animationDelay: `${index * 0.15}s` }}
                >
                  <div className="w-10 h-10 border border-white/40 bg-white/10 backdrop-blur-sm flex items-center justify-center transition-all duration-300">
                    <stat.icon className="w-4 h-4 text-white" />
                  </div>
                  <div className="text-center">
                    <p 
                      className="text-base text-white font-medium"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {stat.isNumber ? (
                        <AnimatedCounter end={stat.value} suffix={stat.suffix || ""} duration={2000} />
                      ) : (
                        stat.value
                      )}
                    </p>
                    <p className="text-[9px] text-white/70 uppercase tracking-wider">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Buttons - Same style as desktop */}
            <div className="flex flex-col gap-3 animate-fade-in-up animation-delay-600 px-2">
              {openLastMinuteClub && (
                <Button
                  onClick={openLastMinuteClub}
                  className="w-full bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 hover:from-amber-400 hover:to-amber-300 text-black uppercase tracking-wider text-xs px-4 py-4 rounded-none transition-all duration-300 border-2 border-amber-300 shadow-[0_0_20px_rgba(251,191,36,0.35)] animate-pulse-glow relative overflow-hidden flex items-center justify-center gap-2 font-bold"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                  data-testid="hero-last-minute-club-mobile"
                >
                  <Zap className="w-4 h-4" />
                  {language === "fr" ? "Club Dernière Minute" : "Last Minute Club"}
                  <span className="ml-1 px-2 py-0.5 bg-black text-amber-300 text-[10px] font-bold rounded-full animate-pulse">
                    {lastMinuteCount > 0
                      ? `${lastMinuteCount} ${language === "fr" ? "places" : "spots"}`
                      : (language === "fr" ? "Voir" : "View")}
                  </span>
                </Button>
              )}
              <Button
                onClick={openVirtualConsultation}
                variant="outline"
                className="w-full border-2 border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-black uppercase tracking-wider text-xs px-4 py-4 rounded-none transition-all duration-300 bg-transparent flex items-center justify-center gap-2 animate-pulse-glow"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="hero-virtual-consultation-mobile"
              >
                <Video className="w-4 h-4" />
                {language === "fr" ? "Consultation Virtuelle" : "Virtual Consultation"}
                <span className="ml-1 px-1.5 py-0.5 bg-amber-500 text-black text-xs font-bold rounded animate-pulse">FREE</span>
              </Button>
              <Button
                onClick={() => scrollToSection("services")}
                variant="outline"
                className="w-full border-2 border-white text-white hover:bg-white hover:text-black uppercase tracking-wider text-xs px-4 py-4 rounded-none transition-all duration-300 bg-transparent"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="hero-view-services-mobile"
              >
                {t("hero.viewServices")}
              </Button>
              <Button
                onClick={() => { navigate("/book/menu"); window.scrollTo(0, 0); }}
                className="w-full bg-black hover:bg-gray-900 text-white uppercase tracking-wider text-xs px-4 py-4 rounded-none transition-all duration-300 border-2 border-black"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="hero-book-consultation-mobile"
              >
                {t("hero.bookNow")}
              </Button>
              {/* Prominent Square direct shortcut — most reliable backup */}
              <div className="bg-emerald-500/15 border-2 border-emerald-400/50 rounded-md p-2.5 shadow-md" data-testid="hero-square-direct-mobile">
                <p className="text-emerald-200 text-[10px] uppercase tracking-widest text-center font-semibold mb-1.5">
                  {language === "fr" ? "Ou réservez direct sur Square" : "Or book direct on Square"}
                </p>
                <div className="grid grid-cols-3 gap-1">
                  <a href="https://precision-nurse-injectionist.square.site/" target="_blank" rel="noopener noreferrer"
                    className="text-center py-1.5 px-1 rounded bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-[10px] font-bold transition-colors"
                    data-testid="hero-square-direct-mobile-1">
                    Carleton
                  </a>
                  <a href="https://karine-lauzon-rn.square.site/" target="_blank" rel="noopener noreferrer"
                    className="text-center py-1.5 px-1 rounded bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-[10px] font-bold transition-colors"
                    data-testid="hero-square-direct-mobile-2">
                    Kanata
                  </a>
                  <a href="https://karine-lauzon-rn-ottawa.square.site/" target="_blank" rel="noopener noreferrer"
                    className="text-center py-1.5 px-1 rounded bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-[10px] font-bold transition-colors"
                    data-testid="hero-square-direct-mobile-3">
                    Ottawa
                  </a>
                </div>
              </div>
              <Button
                onClick={openWaitlist}
                variant="outline"
                className="w-full border-2 border-rose-300 text-rose-200 hover:bg-rose-400 hover:text-white uppercase tracking-wider text-xs px-4 py-4 rounded-none transition-all duration-300 bg-rose-500/20 flex items-center justify-center gap-2 font-semibold"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="hero-join-waitlist-mobile"
              >
                <Clock className="w-4 h-4" />
                {language === "fr" ? "Liste d'Attente" : "Join Waitlist"}
              </Button>
            </div>

            {/* Afterpay financing chip — mobile, sits below the CTA stack. */}
            <button
              type="button"
              onClick={() => scrollToSection("afterpay-section")}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#b2fce4]/15 border border-[#b2fce4]/40 active:bg-[#b2fce4]/25 transition-colors"
              data-testid="hero-afterpay-pill-mobile"
            >
              <span className="bg-[#b2fce4] text-black text-[9px] font-bold px-1.5 py-0.5 rounded tracking-wide">
                afterpay
              </span>
              <span className="text-[#b2fce4] text-[11px] font-semibold uppercase tracking-wider">
                {language === "fr" ? "Payez en 4 · Sans intérêt" : "Pay in 4 · Interest-Free"}
              </span>
              <ArrowRight className="w-3 h-3 text-[#b2fce4]" />
            </button>

            {/* Quiz Button */}
            <div className="mt-4 animate-fade-in-up animation-delay-800">
              <button
                onClick={openQuiz}
                className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-all duration-300 group"
              >
                <div className="p-1.5 bg-white/10 rounded-full group-hover:bg-white/20 transition-colors animate-pulse-glow">
                  <Sparkles className="w-3 h-3" />
                </div>
                <span className="text-xs underline-grow">
                  {language === "fr" ? "Quel traitement me convient?" : "Which treatment is right for me?"}
                </span>
              </button>
            </div>

            {/* Trust Strip - Same as desktop */}
            <div className="mt-6 animate-fade-in-up animation-delay-1000 px-2">
              <div className="flex flex-wrap items-center justify-center gap-2 px-3 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20">
                <div className="flex items-center gap-1">
                  <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                  <span className="text-white text-[10px] font-medium">
                    {language === "fr" ? "100+ avis 5 étoiles" : "100+ 5-Star Reviews"}
                  </span>
                </div>
                <div className="w-px h-3 bg-white/30" />
                <div className="flex items-center gap-1">
                  <Shield className="w-3 h-3 text-amber-400" />
                  <span className="text-white text-[10px] font-medium">
                    {language === "fr" ? "Expérimentée" : "Most Experienced"}
                  </span>
                </div>
                <div className="w-px h-3 bg-white/30" />
                <div className="flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-amber-400" />
                  <span className="text-white text-[10px] font-medium">
                    3 Locations
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* ===== DESKTOP LAYOUT (Original) ===== */}
          <div className="hidden md:block">
            {/* Time-Based Greeting */}
            <div className="mb-4 animate-fade-in">
              <span 
                className="text-white/90 text-lg tracking-wide"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {getTimeBasedGreeting()}, {language === "fr" ? "Bienvenue" : "Welcome"}
              </span>
            </div>

            {/* Badge */}
            <div className="mb-8 animate-fade-in">
              <span 
                className="text-amber-400 text-sm tracking-[0.3em] uppercase animate-pulse-glow inline-block px-4 py-2 rounded-full"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                ✦ {t("hero.experience")} ✦
              </span>
            </div>

            {/* Main Heading - Emotional & Transformation-focused
                iPad-optimized (Feb 24 2026): shifted the 7xl bump up to
                the xl breakpoint so iPad landscape (1024–1279 px) sees
                a 6xl title. Prevents the copy from being clipped by the
                fixed floating luxury cards on the right of the viewport. */}
            <h1
              className="text-5xl md:text-5xl lg:text-6xl xl:text-7xl text-white mb-2 animate-fade-in-up leading-tight"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
            >
              {language === "fr" ? "Révélez Votre" : "Reveal Your"} <span className="italic text-gradient-animate">{language === "fr" ? "Meilleure Version" : "Best Self"}</span>
            </h1>
            
            {/* Script subtitle - Emotional transformation */}
            <p
              className="text-4xl md:text-4xl lg:text-5xl xl:text-6xl text-white mb-6 animate-fade-in-up"
              style={{ fontFamily: "'Great Vibes', cursive", fontWeight: 400 }}
            >
              {language === "fr" ? "Naturellement. En Confiance." : "Naturally. Confidently."}
            </p>

            {/* Subheading - Value proposition */}
            <p 
              className="text-base md:text-lg text-white/90 mb-4 animate-fade-in-up animation-delay-200 max-w-2xl mx-auto leading-relaxed"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300 }}
            >
              {language === "fr" 
                ? "Imaginez vous regarder dans le miroir et aimer ce que vous voyez. Des résultats subtils et raffinés qui rehaussent votre beauté naturelle — sans que personne ne sache pourquoi vous rayonnez."
                : "Imagine looking in the mirror and loving what you see. Subtle, refined results that enhance your natural beauty — without anyone knowing why you're glowing."}
            </p>
            
            {/* Trust line */}
            <p 
              className="text-sm md:text-base text-amber-400/90 mb-10 animate-fade-in-up animation-delay-300 max-w-xl mx-auto"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
            >
              {language === "fr" 
                ? "35+ ans d'expertise en soins infirmiers • Une des injectrices les plus expérimentées d'Ottawa"
                : "35+ years of nursing expertise • One of Ottawa's most experienced injectors"}
            </p>

            {/* Stats Row */}
            <div className="flex flex-wrap justify-center gap-6 md:gap-10 mb-10 stagger-fade-in">
              {stats.map((stat, index) => (
                <div 
                  key={index} 
                  className="flex items-center gap-3 stat-item hover-scale cursor-default"
                  style={{ animationDelay: `${index * 0.15}s` }}
                >
                  <div className="w-12 h-12 border border-white/40 bg-white/10 backdrop-blur-sm flex items-center justify-center icon-container transition-all duration-300 hover:bg-white/20">
                    <stat.icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <p 
                      className="text-xl text-white font-medium price-pop"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {stat.isNumber ? (
                        <AnimatedCounter end={stat.value} suffix={stat.suffix || ""} duration={2000} />
                      ) : (
                        stat.value
                      )}
                    </p>
                    <p className="text-xs text-white/70 uppercase tracking-wider">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4 justify-center animate-fade-in-up animation-delay-600">
              {openLastMinuteClub && (
                <Button
                  onClick={openLastMinuteClub}
                  className="bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 hover:from-amber-400 hover:to-amber-300 text-black uppercase tracking-wider text-sm px-6 py-6 rounded-none transition-all duration-300 border-2 border-amber-300 shadow-[0_0_24px_rgba(251,191,36,0.4)] animate-pulse-glow btn-shine btn-magnetic flex items-center gap-2 font-bold relative overflow-hidden group whitespace-nowrap"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                  data-testid="hero-last-minute-club"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out" />
                  <Zap className="w-4 h-4" />
                  {language === "fr" ? "Club Dernière Minute" : "Last Minute Club"}
                  <span className="ml-1 px-2 py-0.5 bg-black text-amber-300 text-[10px] font-bold rounded-full animate-pulse">
                    {lastMinuteCount > 0
                      ? `${lastMinuteCount} ${language === "fr" ? "places" : "spots"}`
                      : (language === "fr" ? "Voir" : "View")}
                  </span>
                </Button>
              )}
              <Button
                onClick={openVirtualConsultation}
                variant="outline"
                className="border-2 border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-black uppercase tracking-wider text-sm px-6 py-6 rounded-none transition-all duration-300 bg-transparent btn-shine btn-magnetic flex items-center gap-2 animate-pulse-glow relative overflow-hidden group whitespace-nowrap"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="hero-virtual-consultation"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-400/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out animate-shimmer"></span>
                <Video className="w-4 h-4 animate-bounce" style={{ animationDuration: '2s' }} />
                {language === "fr" ? "Consultation Virtuelle" : "Virtual Consultation"}
                <span className="ml-1 px-1.5 py-0.5 bg-amber-500 text-black text-xs font-bold rounded animate-pulse">FREE</span>
              </Button>
              <Button
                onClick={() => scrollToSection("services")}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-black uppercase tracking-wider text-sm px-8 py-6 rounded-none transition-all duration-300 bg-transparent btn-shine btn-magnetic whitespace-nowrap"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="hero-view-services"
              >
                {t("hero.viewServices")}
              </Button>
              <Button
                onClick={() => { navigate("/book/menu"); window.scrollTo(0, 0); }}
                className="bg-black hover:bg-gray-900 text-white uppercase tracking-wider text-sm px-8 py-6 rounded-none transition-all duration-300 border-2 border-black btn-shine btn-magnetic ripple whitespace-nowrap"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="hero-book-consultation"
              >
                {t("hero.bookNow")}
              </Button>
              <Button
                onClick={openWaitlist}
                variant="outline"
                className="border-2 border-rose-300 text-rose-200 hover:bg-rose-400 hover:text-white uppercase tracking-wider text-sm px-6 py-6 rounded-none transition-all duration-300 bg-rose-500/20 btn-shine btn-magnetic flex items-center gap-2 font-semibold whitespace-nowrap"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="hero-join-waitlist"
              >
                <Clock className="w-4 h-4" />
                {language === "fr" ? "Liste d'Attente" : "Join Waitlist"}
              </Button>
            </div>

            {/* Afterpay financing chip — desktop, sits right under the main CTA row. */}
            <button
              type="button"
              onClick={() => scrollToSection("afterpay-section")}
              className="mt-4 inline-flex items-center gap-2.5 px-4 py-2.5 rounded-full bg-[#b2fce4]/12 border border-[#b2fce4]/40 hover:bg-[#b2fce4]/20 transition-all group animate-fade-in-up animation-delay-700"
              data-testid="hero-afterpay-pill-desktop"
            >
              <span className="bg-[#b2fce4] text-black text-[10px] font-bold px-2 py-0.5 rounded tracking-wide">
                afterpay
              </span>
              <span className="text-[#b2fce4] text-xs font-semibold uppercase tracking-wider">
                {language === "fr" ? "Payez en 4 · Sans intérêt" : "Pay in 4 · Interest-Free"}
              </span>
              <span className="text-[#b2fce4]/80 text-xs hidden sm:inline">
                {language === "fr" ? "sur tous les traitements" : "on every treatment"}
              </span>
              <ArrowRight className="w-3.5 h-3.5 text-[#b2fce4] group-hover:translate-x-0.5 transition-transform" />
            </button>

            {/* Prominent Square direct shortcut — most reliable backup */}
            <div className="mt-4 max-w-2xl bg-emerald-500/15 border-2 border-emerald-400/50 rounded-lg p-3 shadow-md animate-fade-in-up" data-testid="hero-square-direct-desktop">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <p className="text-emerald-200 text-xs uppercase tracking-widest font-semibold">
                  {language === "fr" ? "Ou réservez direct sur Square →" : "Or book direct on Square →"}
                </p>
                <div className="flex gap-2 flex-wrap">
                  <a href="https://precision-nurse-injectionist.square.site/" target="_blank" rel="noopener noreferrer"
                    className="px-3 py-1.5 rounded bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-xs font-bold transition-colors"
                    data-testid="hero-square-direct-desktop-1">
                    Carleton Place
                  </a>
                  <a href="https://karine-lauzon-rn.square.site/" target="_blank" rel="noopener noreferrer"
                    className="px-3 py-1.5 rounded bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-xs font-bold transition-colors"
                    data-testid="hero-square-direct-desktop-2">
                    Kanata
                  </a>
                  <a href="https://karine-lauzon-rn-ottawa.square.site/" target="_blank" rel="noopener noreferrer"
                    className="px-3 py-1.5 rounded bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-xs font-bold transition-colors"
                    data-testid="hero-square-direct-desktop-3">
                    Ottawa
                  </a>
                </div>
              </div>
            </div>
            
            {/* Quiz Button */}
            <div className="mt-6 animate-fade-in-up animation-delay-800">
              <button
                onClick={openQuiz}
                className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-all duration-300 group"
              >
                <div className="p-2 bg-white/10 rounded-full group-hover:bg-white/20 transition-colors animate-pulse-glow">
                  <Sparkles className="w-4 h-4" />
                </div>
                <span className="text-sm underline-grow">
                  {language === "fr" ? "Quel traitement me convient?" : "Which treatment is right for me?"}
                </span>
              </button>
            </div>

            {/* Trust Strip */}
            <div className="mt-10 animate-fade-in-up animation-delay-1000">
              <div className="inline-flex items-center justify-center gap-3 md:gap-5 px-5 py-3 bg-white/10 backdrop-blur-md rounded-full border border-white/20 whitespace-nowrap">
                <div className="flex items-center gap-1.5">
                  <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                  <span className="text-white text-xs md:text-sm font-medium">
                    {language === "fr" ? "100+ avis 5 étoiles" : "100+ 5-Star Reviews"}
                  </span>
                </div>
                <div className="w-px h-4 bg-white/30" />
                <div className="flex items-center gap-1.5">
                  <Shield className="w-4 h-4 text-amber-400" />
                  <span className="text-white text-xs md:text-sm font-medium">
                    {language === "fr" ? "Une des injectrices les plus expérimentées d'Ottawa" : "One of Ottawa's Most Experienced Injectors"}
                  </span>
                </div>
                <div className="w-px h-4 bg-white/30" />
                <div className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-amber-400" />
                  <span className="text-white text-xs md:text-sm font-medium">
                    Ottawa • Kanata • Carleton Place
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator - Desktop only */}
      <div className="hidden md:block absolute bottom-8 left-1/2 -translate-x-1/2 animate-float">
        <button
          onClick={() => scrollToSection("services")}
          className="p-2 text-white/60 hover:text-white transition-all duration-300 hover:scale-110"
          data-testid="scroll-indicator"
        >
          <ChevronDown className="w-8 h-8" />
        </button>
      </div>
    </section>
  );
};

export default HeroSection;
